import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  BonusApi,
  getAvailablePages,
  getConfigProp,
  hasActionsRequiresVerification,
  isUserNeedPartialVerification,
  isVerificationEnabled,
  SiteConfigManager,
  useApplicationState,
  useSettings,
  useUserMessages,
} from '@atlas/core';
import { availableClientProfileRoutes, staticRoutes } from '@atlas/routing';
import { CLIENT_PROFILE_COMPONENT_TYPES } from '@atlas/routing/build/routes/componentTypes';
import { Button, MenuItem } from '@atlas/ui';

import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import history from 'components/MainRouter/history';

import ClientProfileHeader from './ClientProfileHeader';
import ClientProfileLogout from './ClientProfileLogout';
import ExpandedNavigationItem from './ExpandedNavigationItem';

const VERIFICATION_TAB_TYPES = [CLIENT_PROFILE_COMPONENT_TYPES.VERIFICATION];

const ClientProfileNavigation = ({ clientProfilePath, clientProfileTabs = [] }) => {
  const applicationState = useApplicationState();

  const { account, authenticated, wallet: { currency } = {} } = applicationState;

  const { lang } = useSettings();
  const { unreadMessagesCount } = useUserMessages();

  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const isRegistrationEnabled = SiteConfigManager.getConfig('config.config.enable_registration');
  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');

  const [freeBetsList, setFreeBetsList] = useState([]);

  const shouldFetchFreebets = useMemo(() => {
    return clientProfileTabs?.some(
      page =>
        page.isExpandedOnMobile &&
        page.tabs?.some(tab => tab.type === CLIENT_PROFILE_COMPONENT_TYPES.BONUSES_FREEBET),
    );
  }, [clientProfileTabs]);

  useEffect(() => {
    if (!shouldFetchFreebets || hideSportBonuses) return;

    (async () => {
      try {
        const { freeBets } = await BonusApi.getFreebetBonuses({
          currency,
          language: lang.toUpperCase(),
        });

        setFreeBetsList(freeBets || []);
      } catch (e) {
        setFreeBetsList([]);
      }
    })();
  }, [shouldFetchFreebets, hideSportBonuses, currency, lang]);

  const availableTabs = useMemo(() => {
    const availablePages = getAvailablePages(availableClientProfileRoutes, clientProfileTabs);

    return availablePages.map(page => {
      if (page.type !== CLIENT_PROFILE_COMPONENT_TYPES.BONUSES) {
        return page;
      }

      return {
        ...page,
        tabs: page.tabs.filter(tab => {
          // hide freebet for sport radar project
          if (isSportRadarProject && tab.type === CLIENT_PROFILE_COMPONENT_TYPES.BONUSES_FREEBET) {
            return false;
          }

          // hide freespins in expanded mobile
          if (
            page.isExpandedOnMobile &&
            tab.type === CLIENT_PROFILE_COMPONENT_TYPES.BONUSES_FREESPINS
          ) {
            return false;
          }

          // FREEBET LOGIC
          if (tab.type === CLIENT_PROFILE_COMPONENT_TYPES.BONUSES_FREEBET) {
            // if expanded mobile → depends on API result
            if (page.isExpandedOnMobile) {
              return !!freeBetsList?.length;
            }

            // if NOT expanded mobile → always show (no API dependency)
            return true;
          }

          return true;
        }),
      };
    });
  }, [clientProfileTabs, isSportRadarProject, freeBetsList]);

  if (!authenticated) {
    return (
      <NotificationStatePopup
        buttons={
          <>
            {isRegistrationEnabled && (
              <Link className="secondary" to={staticRoutes.signup}>
                <Button label="Sign up" size="big" type="secondary" />
              </Link>
            )}
            <Link className="primary" to={staticRoutes.signin}>
              <Button label="Sign in" size="big" type="primary" />
            </Link>
          </>
        }
        buttonsClassName="with-navbar"
        message="To see your profile please sign up or sign in"
        title="You are not logged into your account"
      />
    );
  }

  return (
    <div className="user-profile-group user-profile-group--mb-0">
      <div className="user-profile-group-item">
        <ClientProfileHeader />

        {availableTabs.map(({ icon, isExpandedOnMobile, link, name, tabs, type }) => {
          if (VERIFICATION_TAB_TYPES.includes(type) && !isVerificationEnabled(account)) {
            return null;
          }

          const isCustomIcon =
            VERIFICATION_TAB_TYPES.includes(type) &&
            (hasActionsRequiresVerification(account) ||
              isUserNeedPartialVerification(applicationState?.account));

          if (tabs && isExpandedOnMobile) {
            return (
              <ExpandedNavigationItem key={link} link={link} name={name} tabs={tabs} type={type} />
            );
          }

          const onClick = () => history.push(`${clientProfilePath}/${link}`);

          return (
            <MenuItem
              className="menu-item-container"
              customIcon={isCustomIcon && 'error-legitimuz'}
              icon={icon}
              key={link}
              onClick={onClick}
              renderArrow
              text={name}
              withNotification={
                type === CLIENT_PROFILE_COMPONENT_TYPES.MESSAGES && !!unreadMessagesCount
              }
            />
          );
        })}
      </div>

      <ClientProfileLogout />
    </div>
  );
};

ClientProfileNavigation.propTypes = {
  clientProfilePath: PropTypes.string.isRequired,
  clientProfileTabs: PropTypes.array,
};

export default ClientProfileNavigation;
